/* eslint-disable max-lines */
import cx from "classnames";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { ComponentLockRule, useIsComponentLocked } from "../../helpers/commonStatusHelpers";
import {
  GameConceptIdSearchParam,
  PitchConceptIdSearchParam,
} from "../../helpers/dataFetchingHelpers";
import { FormDataValidation, validateFormUxOnly } from "../../helpers/formValidation";
import {
  useProgramBackgroundColorPicker,
  useProgramColorPicker,
} from "../../helpers/useProgramColorPicker";
import { useString, useStrings } from "../../localization/localizationHelpers";
import {
  FormLocalizedSubtitleStringKey,
  FormLocalizedTitleStringKey,
  Forms,
  FormsConfiguration,
  FormsLocalizedStringsConfiguration,
  Programs,
} from "../../models/programsConfig";
import {
  AaaApplicationStep,
  IdXboxApplicationStep,
  OnboardingProgram,
  PitchApplicationStep,
} from "../../models/status";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import {
  getAaaApplicationStep,
  getCurrentProgram,
  getIdXboxApplicationStep,
  getPitchApplicationStep,
  getSubFormData,
} from "../../redux/selectors";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import { ContentBox, ContentBoxIcon } from "../ContentBox";
import { FormCard } from "../FormCard";
import { EditIcon } from "../icons";
import { RightSideWrapper } from "../RightSideWrapper";
import { SeparatorBar } from "../SeparatorBar";
import * as styles from "./FormSummary.module.css";

export type FormSummaryFormCardRule = Readonly<{
  stepsToShow: Set<AaaApplicationStep | IdXboxApplicationStep | PitchApplicationStep>;
  program: OnboardingProgram;
  formCardId: string;
}>;

type FormSummaryProps = Readonly<{
  formName: Forms;
  titleOverride?: string;
  getFormUrl: (subFormId: any) => string;
  unlockRule?: ComponentLockRule;
  lockRightSideText?: string;
  formCardRule?: FormSummaryFormCardRule;
  statusMessage?: string;
}>;

type FormSummaryRowProps = Readonly<{
  formId: string;
  subFormId: string;
  subFormLocStringId: string;
  hideSeparatorBar: boolean;
  getFormUrl: (subFormId: unknown) => string;
}>;

type LockedFormSummaryProps = Readonly<{
  title: string;
  rightSideText?: string;
}>;

const FormSummaryRow = ({
  formId,
  subFormId,
  subFormLocStringId,
  hideSeparatorBar,
  getFormUrl,
}: FormSummaryRowProps) => {
  const [title, edit, getStarted, continueString, someFieldsNeedToBeCompleted] = useStrings(
    subFormLocStringId,
    "edit",
    "getStarted",
    "continue",
    "someFieldsNeedToBeCompleted"
  );
  const [button, setButton] = useState<ReactNode>(null);
  const [metadataText, setMetadataText] = useState<ReactNode>(null);

  const subFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, formId, subFormId)
  );
  const history = useHistory();
  const colorStyle = useProgramColorPicker();
  const backgroundColorStyle = useProgramBackgroundColorPicker(true);

  useEffect(() => {
    const onClick = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const conceptParam =
        urlParams.get(GameConceptIdSearchParam) ?? urlParams.get(PitchConceptIdSearchParam);
      if (conceptParam === "new") {
        // This is a workaround for brand new concept that have not yet been stored in CRM.
        // We replace the URL so that any Back navigation does not make the user land on yet
        // another new concept. They will most likely be back to the Welcome page if any.
        history.replace(getFormUrl(subFormId));
      } else {
        history.push(getFormUrl(subFormId));
      }
    };
    const validateAndRenderButton = async () => {
      const formValidation = await validateFormUxOnly(subFormData, formId, subFormId);
      switch (formValidation) {
        case undefined:
        case FormDataValidation.NonValidEmpty:
          setButton(
            <BasicButton
              as={"button"}
              buttonType={ButtonType.Brand}
              onClick={() => {
                logPageAction(OnboardingPageAction.GetStartedFormButton);
                onClick();
              }}
              className={styles.basicSubformButton}
              role="link"
              aria-label={`${getStarted}. ${title}`}
            >
              {getStarted}
            </BasicButton>
          );
          return;
        case FormDataValidation.NonValidPartiallyFilled:
          setButton(
            <BasicButton
              as={"button"}
              buttonType={ButtonType.Brand}
              onClick={() => {
                logPageAction(OnboardingPageAction.ContinueFormButton);
                onClick();
              }}
              className={styles.basicSubformButton}
              role="link"
              aria-label={`${continueString}. ${title}`}
            >
              {continueString}
            </BasicButton>
          );
          setMetadataText(<p className={styles.subFormMetadata}>{someFieldsNeedToBeCompleted}</p>);
          return;
        case FormDataValidation.Valid:
          setButton(
            <button
              onClick={() => {
                logPageAction(OnboardingPageAction.EditFormButton);
                onClick();
              }}
              className={cx(styles.editButton, colorStyle)}
              role="link"
              aria-label={`${edit}. ${title}`}
            >
              <EditIcon />
              {edit}
            </button>
          );
          return;
      }
    };

    validateAndRenderButton();
  }, [
    continueString,
    edit,
    formId,
    getFormUrl,
    getStarted,
    history,
    subFormData,
    subFormId,
    someFieldsNeedToBeCompleted,
    backgroundColorStyle,
    colorStyle,
    title,
  ]);

  return (
    <>
      <Row>
        <Column>
          <h3 className={styles.subFormTitle}>{title}</h3>
          {metadataText}
        </Column>
        <Column className={styles.subFormRightSideCol}>{button}</Column>
      </Row>
      {!hideSeparatorBar && <SeparatorBar />}
    </>
  );
};

export const LockedFormSummary = ({ title, rightSideText }: LockedFormSummaryProps) => (
  <ContentBox
    titleConfiguration={{
      title: title,
      icon: ContentBoxIcon.Lock,
      disabled: true,
      separatorBar: false,
      rightSideText: rightSideText,
    }}
  />
);

export const FormSummary = ({
  formName,
  getFormUrl,
  unlockRule,
  lockRightSideText,
  formCardRule,
  statusMessage,
}: FormSummaryProps) => {
  const formConfiguration = FormsConfiguration[formName];
  const formLocalizedStringsConfiguration = FormsLocalizedStringsConfiguration[formName];
  const title = useString(formLocalizedStringsConfiguration[FormLocalizedTitleStringKey]);
  const subtitle = useString(formLocalizedStringsConfiguration[FormLocalizedSubtitleStringKey]);
  const isComponentLocked = useIsComponentLocked(unlockRule);

  const currentProgram = useSelector(getCurrentProgram);
  const currentAaaStep = useSelector(getAaaApplicationStep);
  const currentIdXboxStep = useSelector(getIdXboxApplicationStep);
  const currentPitchStep = useSelector(getPitchApplicationStep);
  const currentStepMap = new Map<
    string,
    AaaApplicationStep | IdXboxApplicationStep | PitchApplicationStep
  >([
    [Programs.Aaa, currentAaaStep],
    [Programs.IdXbox, currentIdXboxStep],
    [Programs.Pitch, currentPitchStep],
  ]);
  const currentStep = currentStepMap.get(currentProgram);
  if (isComponentLocked) {
    return <LockedFormSummary title={title} rightSideText={lockRightSideText} />;
  }
  const content = (
    <ContentBox
      titleConfiguration={{
        title: title,
        subtitle: subtitle,
        icon: ContentBoxIcon.GreenCircle,
        separatorBar: true,
        rightSideText: statusMessage,
      }}
    >
      {formConfiguration?.subFormIds.map((subFormId, index) => (
        <FormSummaryRow
          key={`${formName}-subform-${index}`}
          formId={formConfiguration.formId}
          subFormId={subFormId}
          subFormLocStringId={formLocalizedStringsConfiguration[subFormId]}
          getFormUrl={getFormUrl}
          hideSeparatorBar={formConfiguration?.subFormIds.length - 1 === index}
        />
      ))}
    </ContentBox>
  );

  if (!formCardRule || !currentStep) {
    return content;
  }
  if (currentStep != null && formCardRule.stepsToShow.has(currentStep)) {
    return (
      <RightSideWrapper rightSideComponent={<FormCard cardId={formCardRule.formCardId} />}>
        {content}
      </RightSideWrapper>
    );
  }

  return content;
};
